'use client'

import { useState, useEffect } from 'react'
import { useSearchParams, useRouter } from 'next/navigation'

import type { JobDescription, Location } from '@types'

import JobTypeAhead from '@components/inputs/typeahead/job-typeahead'
import LocationTypeAhead from '@components/inputs/typeahead/location-typeahead'
import useLocale from '@hooks/client/useLocale'
import useHttpRequest from '@hooks/useHttpRequest'

import styles from '../jobs.module.scss'

const defaultLocation: Location = {
  name: '',
  location: [],
  displayName: '',
  type: '',
  county: '',
  state: '',
  postcode: '',
  metropolis: '',
  active: true,
  id: '',
}

export default function JobsSearchSection(): JSX.Element {
  const router = useRouter()
  const searchParams = useSearchParams()
  const locale = useLocale()
  const { get } = useHttpRequest()
  const [job, setJob] = useState<JobDescription>({ micro: '', macro: '' })
  const [location, setLocation] = useState<Location>(defaultLocation)
  const [showError, setShowError] = useState(false)
  const [showJobError, setShowJobError] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [typedJobValue, setTypedJobValue] = useState('')
  const [typedLocationValue, setTypedLocationValue] = useState('')
  const [isSearching, setIsSearching] = useState(false)

  const jobTitle = searchParams.get('query')
  const locationName = searchParams.get('location_name')
  const locationId = searchParams.get('location_id')
  const locationState = searchParams.get('location_state')
  // Show breadcrumb if we have a location (job title is optional)
  const hasSearch = locationName || locationId

  // Sync state from URL params whenever they change
  useEffect(() => {
    const query = searchParams.get('query')
    const locationNameParam = searchParams.get('location_name')
    const locationId = searchParams.get('location_id')
    const locationState = searchParams.get('location_state')
    const locationCounty = searchParams.get('location_county')
    const macro = searchParams.get('macro')

    // Update job from URL params
    // Handle both empty string and actual query values
    const queryValue = query || ''
    const currentMicro = job.micro || ''
    if (queryValue !== currentMicro) {
      setJob({ micro: queryValue, macro: macro || '' })
      setTypedJobValue(queryValue) // Also set typed value for display
    }

    // Update location from URL params
    if (locationNameParam || locationId) {
      // Format display name with state if available
      let displayName = locationNameParam || ''
      if (locationState && displayName) {
        // Check if state is already in the location name
        const locationUpper = displayName.toUpperCase()
        const stateUpper = locationState.toUpperCase()
        if (!locationUpper.endsWith(`, ${stateUpper}`) && !locationUpper.endsWith(` ${stateUpper}`)) {
          displayName = `${displayName}, ${locationState.toUpperCase()}`
        }
      }

      const newLocation = {
        name: locationNameParam || '',
        id: locationId || '',
        location: [],
        displayName: displayName,
        type: '',
        county: locationCounty || '',
        state: locationState?.toUpperCase() || '',
        postcode: '',
        metropolis: '',
        active: true,
      }

      // Update if location has changed (check name, id, or state)
      const locationChanged =
        location.id !== newLocation.id ||
        location.name !== newLocation.name ||
        location.state !== newLocation.state ||
        location.displayName !== newLocation.displayName

      if (locationChanged) {
        setLocation(newLocation)
        setTypedLocationValue(displayName) // Also set typed value for display
      }
    } else if (location.name || location.id) {
      // Clear location if URL params don't have location info
      setLocation(defaultLocation)
      setTypedLocationValue('')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]) // Run whenever searchParams change

  const handleSearchClick = async (): Promise<void> => {
    if (isSearching) return // Prevent double clicks

    setIsSearching(true)

    // Try to get typed values from DOM inputs - react-bootstrap-typeahead uses .rbt-input-main
    // Check .rbt-input-main first as it's the actual input element
    const locationTypeahead = document.querySelector('[data-testid="location-typeahead"]')
    const locationInput =
      (locationTypeahead?.querySelector('.rbt-input-main') as HTMLInputElement) ||
      (locationTypeahead?.querySelector('input[type="text"]') as HTMLInputElement) ||
      (locationTypeahead?.querySelector('input') as HTMLInputElement)
    const jobTypeahead = document.querySelector('[data-testid="job-typeahead"]')
    const jobInput =
      (jobTypeahead?.querySelector('.rbt-input-main') as HTMLInputElement) ||
      (jobTypeahead?.querySelector('input[type="text"]') as HTMLInputElement) ||
      (jobTypeahead?.querySelector('input') as HTMLInputElement)

    // Get the actual input value from DOM (most reliable)
    // react-bootstrap-typeahead uses .rbt-input-main for the input
    const jobInputValue = jobInput?.value?.trim() || ''
    const locationInputValue = locationInput?.value?.trim() || ''

    // Use typed values if available, otherwise use location/job objects
    // Prioritize DOM input value as it's the most current
    // The onSearch callback in JobTypeAhead should update job.micro, but we also check typedJobValue
    const jobValue = (jobInputValue || typedJobValue.trim() || job.micro?.trim() || '').trim()
    const typedLocationText = (locationInputValue || typedLocationValue.trim() || '').trim()

    // If user selected from dropdown (location.id exists), use that selection
    // Only call autocomplete API if user typed but didn't select (no location.id)
    let finalLocation = location
    if (location.id && location.name) {
      // User selected from dropdown - use that exact selection
      finalLocation = location
    } else if (typedLocationText && !location.id) {
      try {
        const autocompleteResults = await get<Location[]>(
          `locations/autocomplete?query=${encodeURIComponent(typedLocationText)}`,
          { service: 'web' },
        )
        if (autocompleteResults && autocompleteResults.length > 0) {
          // Use the first result from autocomplete (same as what /us/jobs would use)
          const firstResult = autocompleteResults[0]
          finalLocation = {
            name: firstResult.name,
            id: firstResult.id,
            type: firstResult.type,
            location: firstResult.location?.map((p) => p.toString()),
            displayName: firstResult.displayName,
            metropolis: firstResult.metropolis,
            county: firstResult.county,
            state: firstResult.state?.toUpperCase() || '',
            postcode: firstResult.postcode,
            active: firstResult.active,
          }
        } else {
          // If no autocomplete results, use typed text as location_name only
          finalLocation = {
            ...defaultLocation,
            name: typedLocationText,
            displayName: typedLocationText,
          }
        }
      } catch (error) {
        console.error('Error fetching location autocomplete:', error)
        // If API call fails, use typed text as location_name only
        finalLocation = {
          ...defaultLocation,
          name: typedLocationText,
          displayName: typedLocationText,
        }
      }
    } else if (!typedLocationText && location.name) {
      finalLocation = location
    }

    const locationValue = finalLocation.displayName || finalLocation.name || typedLocationText
    const isLocationMissing = !locationValue && !finalLocation?.id?.trim()

    setShowJobError(false) // Job title is optional
    setShowError(isLocationMissing)

    if (isLocationMissing) {
      setErrorMessage('Please enter a location to search.')
      setIsSearching(false)
      return
    }

    setErrorMessage('')

    // Build URL params - match exactly what /us/jobs uses via getSerpQueryString
    const params = new URLSearchParams()
    // Only set query if job title is provided (it's optional)
    if (jobValue) {
      params.set('query', jobValue)
      if (job.macro?.trim()) {
        params.set('macro', job.macro.trim())
      }
    }
    // Always set location_name
    params.set('location_name', locationValue)
    // Set location_id if we have it (from selection or autocomplete)
    if (finalLocation.id?.trim()) {
      params.set('location_id', finalLocation.id.trim())
    }
    // Set location_state if available
    if (finalLocation.state?.trim()) {
      params.set('location_state', finalLocation.state.trim().toUpperCase())
    }
    if (finalLocation.county?.trim()) {
      params.set('location_county', finalLocation.county.trim())
    }

    // Set default filter values
    params.set('full_time', 'true')
    params.set('part_time', 'true')
    params.set('no_experience', 'false')
    params.set('sort', 'FEATURED')
    params.set('page', '1')
    params.set('remote', 'false')
    // Use 40.234 km (25 miles) as max radius, same as /us/jobs
    params.set('max_radius', '40.234')

    // Use replace instead of push to update URL without adding to history
    // This ensures the component re-renders with new search params
    router.replace(`/${locale}/jobs?${params.toString()}`, { scroll: false })
    setIsSearching(false)
  }

  const titleize = (value: string): string =>
    value
      .split(' ')
      .filter(Boolean)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')

  // Format location - check if state is already in the location name
  const getFormattedLocation = (): string => {
    if (!locationName) return ''

    const titleizedLocation = titleize(locationName)

    // Check if location name already contains the state (e.g., "San Jose, CA")
    if (locationState) {
      const locationUpper = locationName.toUpperCase()
      const stateUpper = locationState.toUpperCase()

      // If location already ends with the state, don't add it again
      if (locationUpper.endsWith(`, ${stateUpper}`) || locationUpper.endsWith(` ${stateUpper}`)) {
        return titleizedLocation
      }

      // Otherwise, add the state (uppercase)
      return `${titleizedLocation}, ${locationState.toUpperCase()}`
    }

    return titleizedLocation
  }

  const formattedLocation = getFormattedLocation()

  return (
    <section className={styles.jobsSearchSection}>
      <div className={styles.jobsSearchContainer}>
        {hasSearch && (
          <nav className={styles.jobsBreadcrumb}>
            <a className={styles.jobsBreadcrumbLink} href={`/${locale}`}>
              Home
            </a>
            <span className={styles.jobsBreadcrumbSeparator}>/</span>
            <a className={styles.jobsBreadcrumbLink} href={`/${locale}/jobs`}>
              Jobs
            </a>
            {formattedLocation && (
              <>
                <span className={styles.jobsBreadcrumbSeparator}>/</span>
                <span className={styles.jobsBreadcrumbCurrent}>{formattedLocation}</span>
              </>
            )}
            {jobTitle && (
              <>
                <span className={styles.jobsBreadcrumbSeparator}>/</span>
                <span className={styles.jobsBreadcrumbCurrent}>{titleize(jobTitle)}</span>
              </>
            )}
          </nav>
        )}
        <div className={styles.jobsSearchForm}>
          <div className={styles.jobsSearchInputWrapper}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className={styles.jobsSearchIcon}
            >
              <circle cx="11" cy="11" r="8"></circle>
              <path d="m21 21-4.3-4.3"></path>
            </svg>
            <JobTypeAhead
              key={`job-${jobTitle || ''}`}
              className={styles.jobsJobTypeahead}
              currentJob={job}
              setJob={(newJob) => {
                setJob(newJob)
                setTypedJobValue(newJob.micro || '')
              }}
              showError={showJobError}
              setShowError={setShowJobError}
              placeholder="Job title, keywords, or company"
            />
          </div>
          <div className={styles.jobsSearchInputWrapper}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className={styles.jobsSearchIcon}
            >
              <path d="M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0"></path>
              <circle cx="12" cy="10" r="3"></circle>
            </svg>
            <LocationTypeAhead
              key={`location-${locationName || ''}-${location.id || ''}-${locationState || ''}`}
              className={styles.jobsLocationTypeahead}
              currentLocation={location}
              setLocation={(newLocation) => {
                setLocation(newLocation)
                setTypedLocationValue(newLocation.name || newLocation.displayName || '')
              }}
              showError={showError}
              setShowError={setShowError}
              placeholder="City, state, or zip code"
            />
          </div>
          <button className={styles.jobsSearchButton} type="button" onClick={handleSearchClick}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className={styles.jobsSearchButtonIcon}
            >
              <circle cx="11" cy="11" r="8"></circle>
              <path d="m21 21-4.3-4.3"></path>
            </svg>
            <span>Search</span>
          </button>
        </div>
        {errorMessage && <div className={styles.jobsSearchError}>{errorMessage}</div>}
      </div>
    </section>
  )
}
